import { PlainButton, TextInput, Tooltip } from "@jugl-web/ui-components";
import { Switch } from "@jugl-web/ui-components/cross-platform/Switch";
import { ContactPackField } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import React, { useEffect, useRef } from "react";
import { ReactComponent as TrashIcon } from "../../assets/trash.svg";

const MAX_FIELD_LABEL_LENGTH = 32;

export interface FieldRowProps {
  field: ContactPackField;
  isLocked?: boolean;
  onChange?: (patch: Partial<ContactPackField>) => void;
  onRemove?: () => void;
  shouldFocusInput?: boolean;
  onFocusApplied?: () => void;
}

export const FieldRow: React.FC<FieldRowProps> = ({
  field,
  isLocked = false,
  onChange,
  onRemove,
  shouldFocusInput = false,
  onFocusApplied,
}) => {
  const { t } = useTranslations();
  const labelInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!shouldFocusInput) {
      return;
    }

    const input = labelInputRef.current;
    if (!input) {
      return;
    }

    input.scrollIntoView();
    input.focus();
    onFocusApplied?.();
  }, [shouldFocusInput, onFocusApplied]);

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <TextInput
          ref={labelInputRef}
          placeholder={t({
            id: "common.enter",
            defaultMessage: "Enter",
          })}
          value={field.title}
          isDisabled={isLocked}
          classNames={
            isLocked
              ? {
                  inputWrapperClassName: "border-solid bg-[#F5F5F7]",
                  inputClassName: "bg-[#F5F5F7]",
                }
              : undefined
          }
          onChange={(e) => onChange?.({ title: e.target.value.trimStart() })}
          maxLength={MAX_FIELD_LABEL_LENGTH}
          lengthIndicator={{ max: MAX_FIELD_LABEL_LENGTH }}
        />
      </div>
      {!isLocked && (
        <Tooltip
          renderTrigger={({ props, ref }) => (
            <PlainButton
              ref={ref}
              {...props}
              onClick={onRemove}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors hover:bg-grey-100"
            >
              <TrashIcon />
            </PlainButton>
          )}
        >
          {t({
            id: "cx-ai-agents-page.collect-leads-remove-field",
            defaultMessage: "Remove field",
          })}
        </Tooltip>
      )}
      <div className="flex shrink-0 items-center gap-2">
        <Switch
          size="md"
          isChecked={isLocked ? true : field.required}
          isDisabled={isLocked}
          onChange={
            isLocked
              ? undefined
              : (isChecked) => onChange?.({ required: isChecked })
          }
        />
        <span className="font-secondary text-sm leading-[150%] text-dark-800">
          {t({
            id: "cx-ai-agents-page.collect-leads-field-required-short",
            defaultMessage: "Required",
          })}
        </span>
      </div>
    </div>
  );
};
